// import React
import React, { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Icon from "@mui/material/Icon";
import IconButton from '@mui/material/IconButton';
import FormHelperText from '@mui/material/FormHelperText';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from "@mui/material/CircularProgress";
import EditInvoiceForm from "examples/EditInvoiceForm";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import TenantsService from 'services/tenants-service';
import leasesService from 'services/leases-service';
import leaseInvoiceService from 'services/lease-invoice-service';

function EditInvoice(props) {
  const [errors, setErrors] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [allowCustom, setAllowCustom] = useState(false);
  const [customDateStart, setCustomDateStart] = useState("");
  const [customDateEnd, setCustomDateEnd] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [dateError, setDateError] = useState("");
  const [startOfMonth, setStartOfMonth] = useState("");
  const [endOfMonth, setEndOfMonth] = useState("");

  const { open, setOpen, setOpenNotification, rowData, setIsShow, setIsEdited, setErrorEdit } = props;

  const validate = () => {
    let isValid = true;

    // Ensure startOfMonth and endOfMonth are valid before validation
    const minDate = new Date(startOfMonth);
     // end of month and plus 1 next month
    const maxDate = new Date(endOfMonth);
    maxDate.setMonth(maxDate.getMonth() + 1);

    console.log("Validation Dates:", startOfMonth);

    // Validation for invoice amount
    if (!invoiceAmount || invoiceAmount <= 0) {
      setErrorMessage("Invoice amount is required and must be greater than 0.");
      isValid = false;
    } else {
      setErrorMessage(""); // Clear error message if validation passes
    }

    // Validation for custom dates
    if (allowCustom) {
      if (!customDateStart || !customDateEnd) {
        setDateError("Both start and end dates are required.");
        isValid = false;
      } else if (new Date(customDateStart) >= new Date(customDateEnd)) {
        setDateError("Start date must be earlier than end date.");
        isValid = false;
      } else if (new Date(customDateStart) < minDate || new Date(customDateStart) > maxDate) {
        setDateError(`Start date must be between ${startOfMonth} and ${endOfMonth}.`);
        isValid = false;
      } else if (new Date(customDateEnd) < minDate || new Date(customDateEnd) > maxDate) {
        setDateError(`End date must be between ${startOfMonth} and ${endOfMonth}.`);
        isValid = false;
      } else {
        setDateError(""); // Clear date error if validation passes
      }
    }

    return isValid;
  };

  const onHandleSubmit = async (event) => {
    event.preventDefault();

    // Ask for confirmation before proceeding
    const isConfirmed = window.confirm("Are you sure you want to update the invoice?");
    if (!isConfirmed) {
      return; // Exit if user cancels
    }

    // Trigger validation
    if (!validate()) {
      return; // Prevent form submission if validation fails
    }

    // Prepare payload based on allowCustom state
    const payload = {
      invoice_id: rowData.id, // Include id in the payload
      invoiceAmount,
      ...(allowCustom && !(customDateStart === startOfMonth && customDateEnd === endOfMonth) && { customDateStart, customDateEnd }), // Include custom dates only if allowCustom is enabled and they differ from default dates
    };

    // console.log(allowCustom, customDateStart, customDateEnd, startOfMonth, endOfMonth, payload);
    // return;
    try {
      const response = await leaseInvoiceService.updateCustomInvoice(payload);
      console.log(response);
      setOpenNotification(true);
      setOpen(false);
      setIsEdited(true);
      setErrorEdit(false);
    } catch (error) {
      setError(error.message);
      setIsEdited(false);
      setErrorEdit(true);
      console.log('Request failed:', error);
    }
  };

  const handleClose = () => {
    setOpen(false)
  }

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);  // Clear the error after 4 seconds
      }, 5000);

      // Cleanup the timer on component unmount or when error is cleared
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (open) {
      const fetchInvoiceDetails = async () => {
        try {
          const response = await leaseInvoiceService.getInvoiceDetails(rowData.id);

          /*
          Sample keys:
          id, paid_on, period_name: 2025-06-01 00:00:00, summary: {invoice_amount_number: 5000}, remarks: null | date_start date_end
          */
          // console.log("Invoice Details:", response);

          // Set default value for invoiceAmount
          if (response && response.summary && response.summary.invoice_amount_number) {
            setInvoiceAmount(response.summary.invoice_amount_number);
          }

          let setDateDefault = false;
          // Check remarks and set custom dates if applicable
          if (response && response.remarks) {
            const [start_date, end_date] = response.remarks.split("|");
            if (start_date && end_date) {
              setDateDefault = true; // Custom dates are provided
              await setAllowCustom(true); // Enable custom date
              await setCustomDateStart(start_date);
              await setCustomDateEnd(end_date);
            }
          } else {
            setAllowCustom(false); // Disable custom date by default
          }

          
          if (response && response.period_name) { // Set default custom dates based on period_name
            const periodDate = new Date(response.period_name.split(" ")[0]); // Extract date part only
            const month = periodDate.getMonth(); // Months are 0-indexed in JavaScript
            const year = periodDate.getFullYear();
            const start = new Date(year, month, 2).toISOString().split("T")[0];
            const end = new Date(year, month + 1, 1).toISOString().split("T")[0];
            setStartOfMonth(start); // Start of the month
            setEndOfMonth(end); // End of the month
            // if custom date start and custom date end are not set, set them to start and end of the month
            if (!customDateStart && !customDateEnd && !setDateDefault) {
              setCustomDateStart(start);
              setCustomDateEnd(end);
            }
          }
        } catch (error) {
          console.error("Failed to fetch invoice details:", error);
        }
      };
      fetchInvoiceDetails();
    }
  }, [open, rowData]);

  return (
    <Dialog open={open} scroll='paper' onClose={handleClose} aria-labelledby="scroll-dialog-title"  fullWidth={true} disableScrollLock={false} maxWidth='md'>
      <DialogTitle id="scroll-dialog-title">
      <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Icon >close</Icon>
        </IconButton>
        <MDBox
          mx={2}
          py={3}
          px={2}
          variant="gradient"
          bgColor="success"
          borderRadius="lg"
          coloredShadow="info"
          sx={{ marginTop: '30px' }}
        >
          <MDTypography variant="h6" color="white">
              Edit Invoice
          </MDTypography>
        </MDBox>
        </DialogTitle>
      <DialogContent>
        <EditInvoiceForm
          loading={loading}
          loadingButton={loadingButton}
          errorMessage={errorMessage}
          errors={errors}
          onHandleSubmit={onHandleSubmit}
        />
        <MDBox mt={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Invoice Amount"
                type="number"
                fullWidth
                value={invoiceAmount}
                onChange={(e) => setInvoiceAmount(e.target.value)}
              />
            </Grid>
            {errorMessage && (
              <Grid item xs={12}>
                <Alert severity="error">{errorMessage}</Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <MDBox display="flex" alignItems="center">
                <MDTypography variant="body2" mr={2}>
                  Allow Custom date:
                </MDTypography>
                <MDButton
                  variant={allowCustom ? "contained" : "outlined"}
                  color="info"
                  size="small"
                  onClick={() => setAllowCustom(!allowCustom)}
                >
                  {allowCustom ? "Enabled" : "Disabled"}
                </MDButton>
              </MDBox>
            </Grid>
            {allowCustom && (
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Custom Start Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={customDateStart}
                    onChange={(e) => setCustomDateStart(e.target.value)}
                    inputProps={{
                      min: startOfMonth,
                    }}
                    sx={{
                      "& input[type='date']::-webkit-calendar-picker-indicator": {
                        filter: `brightness(0.5)`, // Adjust brightness for disabled dates
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Custom End Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={customDateEnd}
                    onChange={(e) => setCustomDateEnd(e.target.value)}
                    inputProps={{
                      min: startOfMonth,
                    }}
                    sx={{
                      "& input[type='date']::-webkit-calendar-picker-indicator": {
                        filter: `brightness(0.5)`, // Adjust brightness for disabled dates
                      },
                    }}
                  />
                </Grid>
              </>
            )}
            {dateError && (
              <Grid item xs={12}>
                <Alert severity="error">{dateError}</Alert>
              </Grid>
            )}
          </Grid>
        </MDBox>
      </DialogContent>
      <MDBox mt={3} display="flex" justifyContent="flex-end" px={3} pb={3}>
        <MDButton
          variant="contained"
          color="success"
          onClick={onHandleSubmit}
          disabled={loadingButton}
        >
          {loadingButton ? <CircularProgress size={24} color="inherit" /> : "Update Invoice"}
        </MDButton>
      </MDBox>
      {error && <Alert severity="error">{error}</Alert>} 
    </Dialog>
  );
}

export default EditInvoice;
